/**
 * MessageBanner
 * Renders a server-driven in-app message as a banner using the AppBanner store.
 * Triggers the existing AppBanner with the resolved i18n text and registers dismiss on auto-close.
 */

import { useEffect } from 'react';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { useMessageStore } from '../../store/messageStore';
import { resolveMessageTexts } from '../../utils/messageI18n';
import { openURL } from '../../utils/linking';
import type { InAppMessagePayload } from '../../generated/api/http-contracts';

interface Props {
  message: InAppMessagePayload;
}

export function MessageBanner({ message }: Props) {
  const showBanner = useAppBannerStore((s) => s.showBanner);
  const dismissMessage = useMessageStore((s) => s.dismissMessage);

  useEffect(() => {
    const texts = resolveMessageTexts(message.i18nKey);

    showBanner({
      message: texts.body || message.i18nKey,
      type: 'info',
      action: message.actionType && texts.action ? {
        label: texts.action,
        onPress: () => {
          if (message.actionPayload) {
            void openURL(message.actionPayload);
          }
          dismissMessage(message.id);
        },
      } : undefined,
    });

    // Dismiss server-side when banner auto-closes
    const timer = setTimeout(() => {
      dismissMessage(message.id);
    }, 3000); // matches info auto-dismiss

    return () => clearTimeout(timer);
    // Only run once on mount — message is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id]);

  // Renders nothing — delegates to the global AppBanner
  return null;
}
